/*
 * 会话历史 + 消息检索。
 *
 * 旧能力：打开历史会话、按文本/标签/时间/评分搜索、消息评分与收藏。
 *
 * ## 搜索是这一页的主功能，不是装饰
 *
 * 这个搜索框曾经是**没有 onClick 的死按钮**——服务端那时没有用户端检索接口，
 * 输入框摆在那里但按下去什么都不会发生。现在接的是
 * `GET /api/v1/app/me/messages/search`。
 *
 * ⚠ 它是**子串匹配，不是分词匹配**：服务端走 ILIKE，因为现有的全文索引用
 * `simple` 分词，整句中文会切成一个 token，对中文恒无结果（
 * `message-search.app-query.ts` 有完整说明）。所以搜「生日快乐」找不到
 * 「生日的时候很快乐」。**文案上不要暗示它懂语义**，那会让用户以为搜不到
 * 就是没聊过。
 *
 * ## 搜到之后去哪
 *
 * 跳回忆点滴：有记忆碎片就到第三层（那段对话的正文），没有就只到那一天。
 * 服务端已经把 `date` 与 `memoryId` 算好，这里不从时间戳推日期——
 * 那需要知道业务时区，客户端不知道。
 *
 * 结果里**不做本地高亮**：命中的是子串，用 `text.split(q)` 去染色在
 * 大小写不同、或关键词跨越省略号时会错位。宁可不染。
 *
 * ## 连不上时说清楚缺了什么
 *
 * 全部依赖服务端检索，本地没有可用的副本——H5 只缓存能重建的页面数据，
 * 不在客户端存一份消息全量。
 */

import { useCallback, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { LoadingState, PageNotice } from "../../components/AsyncState";
import { Button, PageShell, Section } from "../../components/ui";
import { IconHistory, IconRetry, IconClose } from "../../components/Icons";
import {
  fetchConversations,
  searchMessages,
  type ConversationList,
  type MessageSearchResult,
} from "../../services/me-api";
import { useAppearance } from "../../state/session";
import { useResource } from "../../state/resource";
import "../pages.css";

export function HistoryPage() {
  const appearance = useAppearance();

  /*
   * `keyword` 是输入框里的字，`submitted` 是真正在搜的那个词。
   *
   * 两个分开是刻意的：不做输入即搜。每敲一个字都发一次 ILIKE 查询，
   * 对最重的那个用户是每次 200ms 的全关系扫描，而且中文输入法在拼音
   * 未上屏时也会触发 change。要搜就按一下。
   */
  const [keyword, setKeyword] = useState("");
  const [submitted, setSubmitted] = useState("");

  const list = useResource<ConversationList>((signal) => fetchConversations(signal), []);
  const ready = list.state.phase === "ready" ? list.state.data : null;

  /*
   * 首屏那一页由 useResource 管，翻出来的后续页面累加在这里。
   * 与 FavoritesPage / MemoriesPage 同一套写法。
   */
  const [morePages, setMorePages] = useState<ConversationList["conversations"]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);

  const conversations = [...(ready?.conversations ?? []), ...morePages];

  /*
   * 还有没有下一页用**已加载条数**跟 total 比，不能用第一页的 hasMore：
   * 那个值描述的是「第一页之后还有没有」，翻到底之后它仍然是 true。
   */
  const convTotal = ready?.total ?? 0;
  const hasMoreConversations = conversations.length < convTotal;

  const loadMoreConversations = useCallback(() => {
    setLoadingMore(true);
    setMoreFailed(false);
    fetchConversations(undefined, conversations.length)
      .then((page) => setMorePages((prev) => [...prev, ...page.conversations]))
      .catch(() => setMoreFailed(true))
      .finally(() => setLoadingMore(false));
  }, [conversations.length]);

  /*
   * 没搜时返回 null 而不是不发请求——useResource 没有「跳过」的语义，
   * 换个 hook 只为省这一次同步 resolve 不值得。deps 变化时它会 abort 上一次，
   * 连着改关键词不会有过期结果盖上来。
   */
  const search = useResource<MessageSearchResult | null>(
    (signal) => (submitted ? searchMessages(submitted, signal) : Promise.resolve(null)),
    [submitted],
  );
  const hits = search.state.phase === "ready" ? search.state.data : null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(keyword.trim());
  };

  const clear = () => {
    setKeyword("");
    setSubmitted("");
  };

  return (
    <PageShell
      icon={<IconHistory size={20} />}
      title="会话历史"
      help={
        <>
          <p>
            你和{appearance.name}聊过的每一次都在这里排着队，想找哪一天的话，
            搜一下就能翻出来啦。
          </p>
          <p>
            搜的是原话里的字，你说的和她说的都算。
            不过它只会一字不差地找，换个说法就找不到了～
          </p>
        </>
      }
      actions={
        list.state.phase === "failed" ? (
          <Button tone="quiet" icon={<IconRetry size={15} />} onClick={list.reload}>
            重试
          </Button>
        ) : undefined
      }
    >
      <form className="search-bar" onSubmit={submit}>
        <input
          className="field"
          value={keyword}
          placeholder="搜聊过的原话…"
          onChange={(event) => setKeyword(event.target.value)}
        />
        {submitted ? (
          <Button tone="quiet" icon={<IconClose size={15} />} onClick={clear}>
            清空
          </Button>
        ) : null}
        <Button type="submit" tone="primary" disabled={keyword.trim().length === 0}>
          搜索
        </Button>
      </form>

      {submitted ? (
        <Section
          title={`「${submitted}」`}
          {...(hits ? { hint: `找到 ${hits.total} 条` } : {})}
        >
          {search.state.phase === "loading" ? (
            <LoadingState label="正在翻找" />
          ) : null}

          {search.state.phase === "failed" ? (
            <PageNotice
              scene="history"
              error={search.state.error}
              onRetry={search.reload}
              expected={["命中关键词的每一句原话，以及它发生在哪一天"]}
            />
          ) : null}

          {hits && hits.hits.length === 0 ? (
            <p className="inline-hint">
              没找到含「{submitted}」的话。它只会一字不差地找，
              换几个字或者换个说法再试试。
            </p>
          ) : null}

          {hits && hits.hits.length > 0 ? (
            <>
              <div className="timeline">
                {hits.hits.map((hit) => (
                  <Link
                    key={hit.id}
                    // 有碎片就直接落到那段对话，没有就只能到那一天。
                    to={
                      hit.memoryId
                        ? `/memories/${encodeURIComponent(hit.date)}/${hit.memoryId}`
                        : `/memories/${encodeURIComponent(hit.date)}`
                    }
                    className="timeline__item"
                  >
                    <span className="timeline__head">
                      <span className="timeline__date">{hit.date}</span>
                      <span className="chip">
                        {hit.role === "user" ? "你说" : `${appearance.name}说`}
                      </span>
                    </span>
                    <span className="timeline__text">{hit.text}</span>
                  </Link>
                ))}
              </div>

              {hits.hasMore ? (
                <p className="inline-hint">
                  还有更早的没显示。换个更具体的词能更快找到那一条。
                </p>
              ) : null}
            </>
          ) : null}
        </Section>
      ) : (
        <Section
          title="全部会话"
          // exactOptionalPropertyTypes：hint 不接受显式 undefined，用条件展开。
          {...(ready && convTotal > conversations.length
            ? { hint: `共 ${convTotal} 次，已显示 ${conversations.length} 次` }
            : {})}
        >
          {list.state.phase === "loading" ? (
            <LoadingState label="正在读取会话列表" />
          ) : null}

          {list.state.phase === "failed" ? (
            <PageNotice
              scene="history"
              error={list.state.error}
              onRetry={list.reload}
              expected={[
                "每次会话的起止时间、消息条数与自动生成的标题",
                "会话摘要（ChatMemory）与它抽出的关键词标签",
                "这次会话让好感度变化了多少",
                "被收藏的消息，以及它们参与了哪些记忆",
              ]}
            />
          ) : null}

          {ready && conversations.length === 0 ? (
            <p className="inline-hint">还没有任何一次对话。</p>
          ) : null}

          {ready ? (
            <ul className="record-list">
              {conversations.map((item) => (
                <li key={item.id} className="record-list__item">
                  {/*
                    没总结过的会话没有标题。这里显示「还没总结」而不是编一个——
                    「有没有总结过」本身就是用户看得到的状态。
                  */}
                  <span className="record-list__title">
                    {item.title ?? "还没总结"}
                  </span>
                  <span className="record-list__meta">
                    {new Date(item.startedAt).toLocaleString("zh-CN")} ·{" "}
                    {item.messageCount} 条
                    {/* 未总结时 affinityDelta 是 null，不是 0，两者含义不同。 */}
                    {item.affinityDelta !== null && item.affinityDelta !== 0
                      ? ` · 好感 ${item.affinityDelta > 0 ? "+" : ""}${item.affinityDelta}`
                      : ""}
                  </span>
                  {item.summary ? (
                    <span className="record-list__meta">{item.summary}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}

          {hasMoreConversations ? (
            <Button tone="quiet" onClick={loadMoreConversations} disabled={loadingMore}>
              {loadingMore ? "正在翻更早的…" : "看看更早的会话"}
            </Button>
          ) : null}

          {moreFailed ? (
            <p className="inline-hint">刚才没翻动，再点一次试试。</p>
          ) : null}
        </Section>
      )}
    </PageShell>
  );
}
